import type {
  UsageEventsResponse,
  UsageEventType,
  UsageEventDto,
  QuotaIngestDimension,
} from "@/types";

/** Summary map entry used when a kind has no events in the window. */
const ZERO_DIMENSION: QuotaIngestDimension = { seconds: 0, count: 0 };

/**
 * Whole-month totals for one event kind. The summary is an open map —
 * kinds with no events are absent, which bills zero.
 */
export function summaryFor(
  summary: UsageEventsResponse["summary"] | undefined,
  kind: UsageEventType,
): QuotaIngestDimension {
  return summary?.[kind] ?? ZERO_DIMENSION;
}

/** All billable seconds across every kind — unknown future kinds included. */
export function totalSummarySeconds(
  summary: UsageEventsResponse["summary"] | undefined,
): number {
  return Object.values(summary ?? {}).reduce(
    (sum, dimension) => sum + (dimension?.seconds ?? 0),
    0,
  );
}

/**
 * Billed duration for a single ledger event. 0-second events must stay
 * visibly zero — the ledger bills them (count parity) even without a
 * duration, so they are never rounded up.
 */
export function formatBilledDuration(seconds: number): string {
  if (seconds === 0) return "0 s";
  if (seconds < 60) return "< 1 m";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes} m`;
  return `${hours} h ${String(minutes).padStart(2, "0")} m`;
}

/** UTC `YYYY-MM` for a date — month windows are UTC everywhere in billing. */
export function monthParamFor(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonthParam(month: string, delta: number): string {
  const [year, monthNumber] = month.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, monthNumber - 1 + delta, 1));
  return monthParamFor(shifted);
}

export function isValidMonthParam(value: string | null): value is string {
  return value !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

export function monthLabel(month: string, locale: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

export interface IngestEventDayGroup {
  /** `YYYY-MM-DD` (UTC) — day membership follows billing, not local time. */
  day: string;
  items: UsageEventDto[];
}

/** Groups an ordered event list into consecutive UTC-day buckets. */
export function groupEventsByDay(
  events: UsageEventDto[],
): IngestEventDayGroup[] {
  const groups: IngestEventDayGroup[] = [];
  for (const event of events) {
    const day = event.occurredAt.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(event);
    } else {
      groups.push({ day, items: [event] });
    }
  }
  return groups;
}
